/** @file createServerEditorStore: the EditorStore over the Pose3D server (optimistic batches, live sync). */
import type { Pose3DClient } from '@zilo/pose3d-api/client/createClient';
import { createStore } from 'zustand/vanilla';
import type { EditorActions, EditorState, EditorStore, PreviewLoader } from '../state/editorTypes';
import { initialEditorState } from '../state/initialEditorState';
import { uiActions } from '../state/uiActions';
import { apiAdapter } from './apiAdapter';
import { batchActions } from './batchActions';
import type { Timers } from './previewSchedule';
import { previewSchedule } from './previewSchedule';
import { puppetLoading } from './puppetLoading';
import { refreshActions } from './refreshActions';
import type { ServerContext } from './serverContext';
import { templateClipActions } from './templateClipActions';

/**
 * The api client, the preview builder (null until the viewport's loader is wired: previews are then skipped), a clock
 * for local command application, the live actor name (default `editor`), the refresh debounce (default 150 ms) and timers.
 */
export type ServerStoreOptions = {
  client: Pose3DClient;
  previewLoader: PreviewLoader | null;
  now?: () => number;
  actor?: string;
  debounceMs?: number;
  timers?: Timers;
};

/**
 * `dispatch` applies at once and sends `{ commands, ifRevision }`; a refused or failed batch (`ok: false`, STALE_REV,
 * network) restores the pre-batch document, sets `lastError` and shows one toast. Commits from other actors arrive on
 * the live socket and are applied on top; after any commit the document is resynced, the preview rebuilt and lint
 * refetched (debounced). `undo` and `redo` are the server's (`puppet.history`, read after every commit, names the next
 * step each way); `closePuppet` closes the socket and drops late answers.
 */
export function createServerEditorStore(options: ServerStoreOptions): EditorStore {
  return createStore<EditorState & EditorActions>()((set, get) => {
    const ctx: ServerContext = {
      set, get, api: apiAdapter(options.client), previewLoader: options.previewLoader, now: options.now ?? (() => Date.now()),
      actor: options.actor ?? 'editor', queue: null, live: null, schedule: { schedule: () => Promise.resolve(), cancel: () => undefined },
      token: 0, dirty: false, chain: Promise.resolve(),
    };
    const refresh = refreshActions(ctx);
    ctx.schedule = previewSchedule(refresh.refreshNow, options.debounceMs ?? 150, options.timers);
    const batches = batchActions(ctx);
    const loading = puppetLoading(ctx, { onCommit: batches.onCommit, refreshNow: refresh.refreshNow });
    return {
      ...initialEditorState(ctx.api.url),
      ...uiActions(set),
      ...loading,
      dispatch: batches.dispatch,
      undo: batches.undo,
      redo: batches.redo,
      refreshPreview: refresh.refreshPreview,
      refreshLint: refresh.refreshLint,
      ...templateClipActions(ctx),
    };
  });
}
